/**
 * Collects the two fields Google does not provide.
 *
 * Shown after a Google sign-in whose account has no affiliation or country
 * yet. Password sign-up already asks for both on the User Access form, so
 * those users never see this.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { setUser } from '../../store/actions/authActions';
import * as authService from '../../services/authService';
import '../start/PasswordGate.css';
import './LoginPage.css';

const focusRowInput = (e) => {
  const input = e.currentTarget.querySelector('input');
  if (input && !input.disabled) input.focus();
};

const CompleteProfilePage = ({ isAuthenticated, bootstrapped, user, setUserAction }) => {
  const history = useHistory();
  const [organization, setOrganization] = useState((user && user.organization) || '');
  const [country, setCountry] = useState((user && user.country) || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (bootstrapped && !isAuthenticated) return <Redirect to="/access" />;
  // Nothing to collect — don't strand the user on a form with no purpose.
  if (bootstrapped && user && user.profileComplete) return <Redirect to="/subscription" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const org = organization.trim();
    const ctry = country.trim();
    if (!org) return setError('Please enter your affiliation.');
    if (!ctry) return setError('Please enter your country.');

    setBusy(true);
    setError('');
    try {
      const data = await authService.updateProfile({ organization: org, country: ctry });
      setUserAction(data.user);
      history.push('/subscription');
    } catch (err) {
      setError(err.message || 'Could not save your details. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="xk-gate">
      <div className="xk-gate__card">
        <img
          src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
          alt="XLog3D"
          className="xk-gate__logo"
        />

        <h2 className="xk-gate__title">Almost there</h2>
        <p className="xk-gate__subtitle">
          Google does not share these, so we need them from you.
        </p>

        <form className="xk-gate__form" onSubmit={handleSubmit} noValidate>
          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="profile-affiliation">Affiliation</label>
            <input
              id="profile-affiliation"
              className="xk-gate__row-input"
              type="text"
              value={organization}
              disabled={busy}
              autoFocus
              onChange={(e) => {
                setOrganization(e.target.value);
                if (error) setError('');
              }}
            />
          </div>

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="profile-country">Country</label>
            <input
              id="profile-country"
              className="xk-gate__row-input"
              type="text"
              value={country}
              disabled={busy}
              onChange={(e) => {
                setCountry(e.target.value);
                if (error) setError('');
              }}
            />
          </div>

          {error && <div className="xk-gate__error">{error}</div>}

          <button type="submit" className="xk-gate__submit" disabled={busy}>
            {busy ? 'Saving...' : 'CONTINUE'}
          </button>
        </form>
      </div>
    </div>
  );
};

CompleteProfilePage.propTypes = {
  isAuthenticated: PropTypes.bool,
  bootstrapped: PropTypes.bool,
  user: PropTypes.object,
  setUserAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isAuthenticated: Boolean(state.auth && state.auth.isAuthenticated),
  bootstrapped: Boolean(state.auth && state.auth.bootstrapped),
  user: state.auth && state.auth.user,
});

export default connect(mapStateToProps, { setUserAction: setUser })(CompleteProfilePage);
